// ContactEditDesktopComposer — desktop right-column composer extracted verbatim
// from ContactEditModal.tsx's modalContent JSX (the `{!viewOnly && (...)}`
// composer block under the activity timeline). Pure presentational leaf: holds
// no state/effects of its own. The orchestrator owns all composer state and the
// raw-fetch send path — this component delegates send via `onSend` (the
// orchestrator's existing sendContactEmail), so NO `fetch` lives here.
//
// Markup/handlers/inline-styles/a11y are byte-identical to the inline original.
import React from 'react';
import { stripHtmlToText } from '../utils';
import { type EmailAttachment } from '../lib/emailAttachments';
import type { Activity } from '../lib/types';

interface GmailMessageLike {
  id: string;
  from: string;
  to: string;
  date: string;
  body: string;
  subject: string;
  threadId: string;
}

export interface ContactEditDesktopComposerProps {
  composerMode: 'reply' | 'compose' | 'note' | null;
  setComposerMode: (m: 'reply' | 'compose' | 'note' | null) => void;
  composerCanReply: boolean;
  composerLastSubject: string;
  connectedMailboxes: string[];
  noteType: 'note' | 'call' | 'email' | 'meet';
  setNoteType: (t: 'note' | 'call' | 'email' | 'meet') => void;
  composeSubject: string;
  setComposeSubject: (s: string) => void;
  composeFrom: string;
  setComposeFrom: (s: string) => void;
  newNote: string;
  setNewNote: (s: string) => void;
  composerFocused: boolean;
  setComposerFocused: (b: boolean) => void;
  emailAttachments: EmailAttachment[];
  setEmailAttachments: React.Dispatch<React.SetStateAction<EmailAttachment[]>>;
  emailFileInputRef: React.RefObject<HTMLInputElement | null>;
  handleEmailFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeEmailAttachment: (id: string) => void;
  aiDrafting: boolean;
  setAiDrafting: (b: boolean) => void;
  gmailMessages: GmailMessageLike[];
  activities: Activity[];
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  firm: string;
  contactTitle: string;
  profile: { name?: string } | null | undefined;
  handleAddNote: () => void;
  onSend: (subjectLine: string) => void;
}

export function ContactEditDesktopComposer({
  composerMode, setComposerMode, composerCanReply, composerLastSubject, connectedMailboxes,
  noteType, setNoteType, composeSubject, setComposeSubject, composeFrom, setComposeFrom,
  newNote, setNewNote, composerFocused, setComposerFocused, emailAttachments, setEmailAttachments,
  emailFileInputRef, handleEmailFileSelect, removeEmailAttachment, aiDrafting, setAiDrafting,
  gmailMessages, activities, name, firstName, lastName, email, company, firm, contactTitle,
  profile, handleAddNote, onSend,
}: ContactEditDesktopComposerProps) {
  return (
    <div style={{ borderTop: '1px solid #DADCE0', flexShrink: 0, position: 'relative', zIndex: 2 }}>
      {/* Collapsed: Reply / Compose / Note buttons */}
      {composerMode === null ? (
        <div style={{ display: 'flex', gap: 8, padding: '10px 12px' }}>
          {composerCanReply && (
            <button type="button" onClick={() => { setNoteType('email'); setComposerMode('reply'); }} style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 600, color: '#3C4043', background: 'var(--color-surface)', border: '1px solid #DADCE0', borderRadius: 8, cursor: 'pointer' }}>Reply</button>
          )}
          <button type="button" onClick={() => { setNoteType('email'); setComposeSubject(''); setComposeFrom(connectedMailboxes.length >= 2 ? (connectedMailboxes[0] || '') : ''); setComposerMode('compose'); }} style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 600, color: '#fff', background: 'var(--color-brand)', border: '1px solid var(--color-brand)', borderRadius: 8, cursor: 'pointer' }}>Compose</button>
          <button type="button" onClick={() => { setNoteType('note'); setComposerMode('note'); }} style={{ flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 600, color: '#3C4043', background: 'var(--color-surface)', border: '1px solid #DADCE0', borderRadius: 8, cursor: 'pointer' }}>Note</button>
        </div>
      ) : (
      <>
      {/* Active composer: header + cancel */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px 0' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
          {composerMode === 'reply' ? 'Reply' : composerMode === 'compose' ? 'New email' : 'Note'}
        </span>
        <button type="button" onClick={() => { setComposerMode(null); setNewNote(''); setComposeSubject(''); setEmailAttachments([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 12 }}>Cancel</button>
      </div>
      {/* Compose: subject + account picker (only when 2+ mailboxes) */}
      {composerMode === 'compose' && (
        <div style={{ padding: '8px 16px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="Subject" style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #DADCE0', borderRadius: 8, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text)' }} />
          {connectedMailboxes.length >= 2 && (
            <select value={composeFrom || connectedMailboxes[0]} onChange={(e) => setComposeFrom(e.target.value)} style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid #DADCE0', borderRadius: 8, boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text)' }}>
              {connectedMailboxes.map(m => <option key={m} value={m}>From: {m}</option>)}
            </select>
          )}
        </div>
      )}
      {/* Reply: which thread we're continuing */}
      {composerMode === 'reply' && composerLastSubject && (
        <div style={{ padding: '6px 16px 0', fontSize: 12, color: 'var(--color-text-muted)' }}>{composerLastSubject}</div>
      )}
      {/* Input area — ChatBox-style bordered container */}
      <div style={{ padding: '12px 16px' }}>
        <input ref={emailFileInputRef} type="file" multiple onChange={handleEmailFileSelect} style={{ display: 'none' }} />
        <div style={{
          border: composerFocused ? '1.5px solid var(--color-brand)' : '1.5px solid #DADCE0',
          borderRadius: 12, overflow: 'hidden', background: 'var(--color-surface)',
          transition: 'border-color 0.15s',
        }}>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder={noteType === 'email' ? 'Compose an email...' : 'Write a note...'}
            rows={composerMode === 'note' ? 3 : 8}
            style={{
              width: '100%', padding: '12px 14px', fontSize: 13,
              border: 'none', resize: 'vertical',
              outline: 'none', fontFamily: 'inherit', background: 'var(--color-surface)', color: 'var(--color-text)',
              lineHeight: 1.5, display: 'block',
            }}
            onFocus={() => setComposerFocused(true)}
            onBlur={() => setComposerFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && noteType === 'note') {
                e.preventDefault();
                handleAddNote();
              }
            }}
          />
          {/* Email attachments preview */}
          {noteType === 'email' && emailAttachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '0 14px 6px' }}>
              {emailAttachments.map(att => (
                <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, border: '1px solid #DADCE0', background: '#F8F9FA', fontSize: 12 }}>
                  {att.previewUrl ? <img src={att.previewUrl} alt="" style={{ width: 16, height: 16, objectFit: 'cover', borderRadius: 2 }} /> : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7a7f85" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  )}
                  <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#3C4043' }}>{att.filename}</span>
                  <span style={{ color: 'var(--color-text-muted)' }}>{att.size < 1048576 ? `${Math.round(att.size / 1024)}KB` : `${(att.size / 1048576).toFixed(1)}MB`}</span>
                  <button onClick={() => removeEmailAttachment(att.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-text-muted)', fontSize: 13, lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
          {/* Toolbar row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderTop: '1px solid #F1F3F4' }}>
            {/* Left: attach button (email only) */}
            <div>
              {noteType === 'email' && (
                <button
                  type="button"
                  onClick={() => emailFileInputRef.current?.click()}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}
                  title="Attach files"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
              )}
            </div>
            {/* Right: AI Draft + Send/Save */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* AI Draft button — only for email tab */}
              {noteType === 'email' && (
                <button
                  type="button"
                  disabled={aiDrafting}
                  onClick={() => {
                    (async () => {
                      setAiDrafting(true);
                      try {
                        const contactName = name || `${firstName} ${lastName}`.trim();
                        const recentEmailsStr = gmailMessages
                          .slice(-5)
                          .map(m => {
                            const stripped = stripHtmlToText(m.body);
                            const who = m.from.toLowerCase().includes(email?.toLowerCase() || '---') ? contactName : 'Me';
                            return `${who}: ${stripped.substring(0, 200)}`;
                          })
                          .join('\n');
                        const recentNotesStr = activities
                          .filter(a => a.type !== 'email')
                          .slice(0, 3)
                          .map(a => a.note.substring(0, 150))
                          .join('\n');

                        const { apiPost } = await import('@/lib/api/client');
                        const { data } = await apiPost<{ draft: string }>('/contacts/draft-email', {
                          contactName,
                          contactCompany: company || firm || '',
                          contactTitle: contactTitle || '',
                          userFirstName: profile?.name?.split(' ')[0] || 'there',
                          existingDraft: newNote.trim() || '',
                          recentEmails: recentEmailsStr,
                          recentNotes: recentNotesStr,
                        });

                        if (data.draft) {
                          setNewNote(data.draft);
                        }
                      } catch (err) {
                        console.error('AI draft failed:', err);
                      } finally {
                        setAiDrafting(false);
                      }
                    })();
                  }}
                  style={{
                    padding: '6px 14px', fontSize: 13, fontWeight: 500,
                    borderRadius: 6, border: '1px solid #DADCE0',
                    cursor: aiDrafting ? 'default' : 'pointer',
                    background: 'var(--color-surface)', color: aiDrafting ? '#7a7f85' : '#3C4043',
                    transition: 'all 0.15s',
                    opacity: aiDrafting ? 0.7 : 1,
                  }}
                >
                  {aiDrafting ? 'Drafting...' : 'AI Draft'}
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (noteType === 'email') {
                    onSend(composerMode === 'compose' ? composeSubject : composerLastSubject);
                  } else if (newNote.trim()) {
                    handleAddNote();
                    setComposerMode(null);
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={!newNote.trim()}
                style={{
                  width: 32, height: 32, borderRadius: 8, border: 'none',
                  cursor: newNote.trim() ? 'pointer' : 'not-allowed',
                  background: newNote.trim() ? 'var(--color-brand)' : '#DADCE0',
                  color: newNote.trim() ? '#fff' : '#7a7f85',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0,
                }}
              >
                {noteType === 'email' ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
